import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage document title for PWA compatibility
 * This hook ensures title updates work properly in both browser and PWA contexts
 */
export const useDocumentTitle = (title: string) => {
  const prevTitleRef = useRef<string>();

  useEffect(() => {
    const prevTitle = prevTitleRef.current;
    
    if (title && title !== prevTitle) {
      // Update document title with multiple methods for PWA compatibility
      const updateTitle = () => {
        document.title = title;
        
        // Try to update the HTML title tag as well
        const titleElement = document.querySelector('title');
        if (titleElement) {
          titleElement.textContent = title;
        }
        
        // Dispatch a custom event for other parts of the app to listen to
        window.dispatchEvent(new CustomEvent('titlechange', { detail: { title } }));
      };
      
      updateTitle();
      
      // For PWAs, also communicate with service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'TITLE_UPDATE',
          title: title
        });
      }
      
      // Force update after a small delay to ensure PWA picks it up
      const timeoutId = setTimeout(updateTitle, 100);
      
      // Store current title
      prevTitleRef.current = title;
      
      // Cleanup timeout
      return () => clearTimeout(timeoutId);
    }
  }, [title]);

  // Listen for service worker messages about title updates
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'TITLE_UPDATED') {
        document.title = event.data.title;
        const titleElement = document.querySelector('title');
        if (titleElement) {
          titleElement.textContent = event.data.title;
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, []);
};

export default useDocumentTitle;
